import type { BadgeVariant } from '@/components/ui/badge'
import { EndorsementQueueView } from '@/models/internalEndorsement'
import {
  CaiUpdate,
  type CorgiChangeOutcome,
  CorgiChangeResult,
  PolicyChangeStatus,
} from '@/models/policyChange'

/** What staff do next at each status, as the console's step badges name it. */
export const STAFF_STATUS_LABELS: Record<PolicyChangeStatus, string> = {
  [PolicyChangeStatus.DRAFT]: 'Draft',
  [PolicyChangeStatus.SUBMITTED]: 'Review',
  [PolicyChangeStatus.NEEDS_INFO]: 'Needs info',
  [PolicyChangeStatus.WITH_CARRIER]: 'With carrier',
  [PolicyChangeStatus.QUOTE_READY]: 'Customer decision',
  [PolicyChangeStatus.ACCEPTED]: 'Bind',
  [PolicyChangeStatus.BOUND]: 'Update policies',
  [PolicyChangeStatus.POLICY_UPDATED]: 'Issue COI',
  [PolicyChangeStatus.COMPLETE]: 'Complete',
  [PolicyChangeStatus.DECLINED]: 'Declined',
  [PolicyChangeStatus.CANCELED]: 'Canceled',
}

export const QUEUE_VIEWS = [
  { value: EndorsementQueueView.NEEDS_ACTION, label: 'Needs action' },
  { value: EndorsementQueueView.WAITING_ON_CUSTOMER, label: 'Waiting on customer' },
  { value: EndorsementQueueView.WITH_CARRIER, label: 'With carrier' },
  { value: EndorsementQueueView.OPEN, label: 'All open' },
  { value: EndorsementQueueView.CLOSED, label: 'Closed' },
]

export const EMPTY_QUEUE: Record<EndorsementQueueView, string> = {
  [EndorsementQueueView.NEEDS_ACTION]: 'Nothing needs you right now.',
  [EndorsementQueueView.WAITING_ON_CUSTOMER]: 'No request is waiting on a customer.',
  [EndorsementQueueView.WITH_CARRIER]: 'No request is out with carriers.',
  [EndorsementQueueView.OPEN]: 'No open requests.',
  [EndorsementQueueView.CLOSED]: 'No finished requests yet.',
}

export const SOURCE_LABELS: Record<'PORTAL' | 'ROSTER' | 'BROKER' | 'ASK_CAI' | 'PHONE', string> = {
  PORTAL: 'the request form',
  ROSTER: 'the Trucks, Trailers and Drivers pages',
  BROKER: 'Cai',
  ASK_CAI: 'Ask Cai',
  PHONE: 'a phone call',
}

/** What staff do at each step; Cai does nothing outside Cai for them. */
export const STAFF_NEXT_STEP: Partial<Record<PolicyChangeStatus, string>> = {
  [PolicyChangeStatus.SUBMITTED]:
    'Cai doesn’t contact the insurance companies. Send each one these changes yourself, by portal, email or phone, then mark it sent.',
  [PolicyChangeStatus.WITH_CARRIER]:
    'Enter each policy’s quote as the insurance companies reply, then post the quotes to the customer.',
  [PolicyChangeStatus.ACCEPTED]:
    'Bind the changes with each insurance company, then mark it bound. Cai makes the Automatic updates below on each change’s date.',
  [PolicyChangeStatus.BOUND]:
    'Do the Manual updates below, then mark the policies updated once each insurance company has issued the change.',
  [PolicyChangeStatus.POLICY_UPDATED]:
    'Issue the updated COI: Cai rebuilds the customer’s certificates from the roster and business details.',
}

/** The review step when a Corgi policy is on the request: Cai drafts that one itself. */
export const STAFF_NEXT_STEP_SUBMITTED_WITH_CORGI =
  'Cai drafts the trucks, trailers and drivers on the Corgi policies when you send the request on. Send any other insurance company the changes yourself first.'

/** The with-carrier step when a Corgi policy is on the request: Corgi's quote arrives on its own. */
export const STAFF_NEXT_STEP_WITH_CARRIER_WITH_CORGI =
  'Submit the Corgi drafts for a quote; Corgi prices within moments and Cai fills its quote in. Enter the other insurance companies’ quotes as they reply, then post the quotes to the customer.'

/** One change's outcome on one Corgi policy, as the Changes table says it. */
export function corgiOutcomeText(outcome: CorgiChangeOutcome): string {
  switch (outcome.outcome) {
    case CorgiChangeResult.PLACED:
      return `On the Corgi draft for ${outcome.policyNumber}.`
    case CorgiChangeResult.REFUSED:
      return `Corgi refused it on ${outcome.policyNumber}: ${outcome.detail}`
    case CorgiChangeResult.NOT_ON_POLICY:
      return `Cai couldn’t find it on ${outcome.policyNumber} on Corgi, so it isn’t on the draft. If it’s still on that policy, remove it in the Corgi portal; if it’s already off, there’s nothing to do.`
    case CorgiChangeResult.UNSUPPORTED:
      return 'Cai cannot make this kind of change on Corgi. Add it in the portal.'
  }
}

/** Whether a change still needs a person in the Corgi portal. */
export const needsPortal = (outcome: CorgiChangeOutcome) => outcome.outcome !== CorgiChangeResult.PLACED

/** Whether Cai updates its own records from a change once it's bound, and what staff do when it doesn't. */
export const CAI_UPDATES: Record<CaiUpdate, { label: string; variant: BadgeVariant; text: string }> = {
  [CaiUpdate.ROSTER]: { label: 'Automatic', variant: 'success', text: 'Cai updates the roster on its date.' },
  [CaiUpdate.BUSINESS_NAME]: {
    label: 'Automatic',
    variant: 'success',
    text: 'Cai updates the business name on its date.',
  },
  [CaiUpdate.MAILING_ADDRESS]: {
    label: 'Automatic',
    variant: 'success',
    text: 'Cai updates the mailing address on its date.',
  },
  [CaiUpdate.ROSTER_BY_HAND]: {
    label: 'Manual',
    variant: 'warning',
    text: 'Make this change on the roster yourself; it was described, not edited.',
  },
  [CaiUpdate.POLICY_DOCUMENT]: {
    label: 'Manual',
    variant: 'warning',
    text: 'Upload the updated policy, so COIs show it.',
  },
  [CaiUpdate.CERTIFICATE_HOLDERS]: {
    label: 'Manual',
    variant: 'warning',
    text: 'Update the customer’s certificate holders.',
  },
  [CaiUpdate.NOTHING]: {
    label: 'Not in Cai',
    variant: 'secondary',
    text: 'Only the insurance company records this.',
  },
}
